import { z } from 'zod';

/**
 * Where a campaign stands (`03-notifications.md` §Campaigns).
 *
 * `sending` is the **claim** (N27): one guarded write moves a campaign out of
 * `draft`/`scheduled` before the first batch is enqueued, so a staff double
 * click, a second scheduler tick and a retried job cannot each start their own
 * walk over the same segment. It is also the status the walk re-reads at the top
 * of every batch — which is how `cancelled` stops the batches that have not run.
 *
 * There is deliberately no `paused`. A campaign that stops mid-walk cannot
 * resume honestly: the segment is resolved as the walk goes, so the population
 * an hour later is a different population, and "resume" would silently mean
 * "send to whoever matches now". Cancelling and composing a new campaign says
 * that out loud.
 */
export const CAMPAIGN_STATUSES = ['draft', 'scheduled', 'sending', 'sent', 'cancelled'] as const;

export const CampaignStatusSchema = z.enum(CAMPAIGN_STATUSES);

export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number];

const STATUS_SET: ReadonlySet<string> = new Set(CAMPAIGN_STATUSES);

export function isCampaignStatus(value: string): value is CampaignStatus {
  return STATUS_SET.has(value);
}

/**
 * Whether the copy, the segment and the schedule may still be changed.
 *
 * Declared here rather than in the api so the composer greys the form and the
 * controller refuses the write from the **same** answer — the shape
 * `isRetriableDeliveryStatus` already keeps for the delivery log's retry action.
 */
export function isEditableCampaignStatus(status: CampaignStatus): boolean {
  return status === 'draft' || status === 'scheduled';
}

/** Whether the walk can still be stopped before its remaining batches run. */
export function isCancellableCampaignStatus(status: CampaignStatus): boolean {
  return status === 'draft' || status === 'scheduled' || status === 'sending';
}

/** Whether the campaign has stopped moving for good. */
export function isTerminalCampaignStatus(status: CampaignStatus): boolean {
  return status === 'sent' || status === 'cancelled';
}
